/**
 * Painel aberto durante uma publicação: as telas são baixadas sob demanda (routes/prefetch.ts) e
 * cada publicação troca os nomes dos arquivos (/assets/Tela-<hash>.js). Quem estava com a aba
 * aberta pede o arquivo antigo, que já não existe; o Workers devolve o index.html no lugar
 * (not_found_handling: single-page-application) e o navegador recusa ("Failed to fetch
 * dynamically imported module"). A saída é recarregar a página, que já vem com os nomes novos.
 *
 * O Vite avisa toda falha de import dinâmico pelo evento `vite:preloadError` (inclusive a do
 * próprio módulo). Recarregamos uma vez; se falhar de novo em 30 s, o erro segue para a tela de
 * erro das rotas (RouteError), para não entrar em laço.
 */
const STORAGE_KEY = 'lm:chunk-reload-at';
const RETRY_WINDOW_MS = 30_000;

let reloading = false;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /dynamically imported module|Importing a module script failed|Failed to load module script|Unable to preload CSS|ChunkLoadError/i.test(
    message,
  );
}

export function isReloadingForUpdate(): boolean {
  return reloading;
}

/** Recarrega a página uma vez. Devolve false quando já tentou há pouco (ou sem sessionStorage). */
export function reloadForUpdate(): boolean {
  if (reloading) return true;
  try {
    const last = Number(window.sessionStorage.getItem(STORAGE_KEY)) || 0;
    if (Date.now() - last < RETRY_WINDOW_MS) return false;
    window.sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // Sem como lembrar da tentativa, não arriscamos um laço de recargas.
    return false;
  }
  reloading = true;
  window.location.reload();
  return true;
}

export function installChunkReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForUpdate()) event.preventDefault();
  });
}
